import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { PopButton } from "../PopButton";
import type { GroupState } from "./GroupParts";

export function OptionsEditor({ group, token, onDone }: { group: GroupState; token: string; onDone: () => void }) {
  const [options, setOptions] = useState<string[]>(group.options.map((o) => o.label));
  const [busy, setBusy] = useState(false);
  const filled = options.map((o) => o.trim()).filter(Boolean);
  const dupes = filled.length !== new Set(filled.map((o) => o.toLowerCase())).size;

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/groups/${group.id}/options`, { options: filled }, { headers: { "X-Host-Token": token } });
      toast.success("Options updated for everyone");
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-xl border-2 border-ink bg-white p-3" data-testid="group-options-editor">
      {options.map((o, i) => (
        <div key={i} className="flex gap-2">
          <input className="field py-2" maxLength={80} value={o} data-testid={`group-edit-option-input-${i}`}
            onChange={(e) => setOptions(options.map((x, j) => (j === i ? e.target.value : x)))} />
          {options.length > 2 && (
            <button aria-label="Remove option" className="rounded-xl border-2 border-ink px-3 hover:bg-pop-pink hover:text-white" onClick={() => setOptions(options.filter((_, j) => j !== i))} data-testid={`group-edit-option-remove-${i}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}
      {dupes && <p className="text-sm font-bold text-[#E11D48]">Two options are the same.</p>}
      {options.length < 12 && (
        <button className="inline-flex items-center gap-1 text-sm font-extrabold uppercase hover:underline" onClick={() => setOptions([...options, ""])} data-testid="group-edit-add-option-button">
          <Plus className="h-4 w-4" strokeWidth={3} /> Add option
        </button>
      )}
      <div className="grid grid-cols-2 gap-2 pt-1">
        <PopButton tone="purple" size="sm" disabled={busy || filled.length < 2 || dupes} onClick={save} data-testid="group-edit-save-button">Save</PopButton>
        <PopButton tone="white" size="sm" onClick={onDone} data-testid="group-edit-cancel-button">Cancel</PopButton>
      </div>
    </div>
  );
}
